import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Ticket } from '../types';
import { transactionService } from '../services/api';
import PrintableTicket from './PrintableTicket';

const ReceiptPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [qrCodeData, setQrCodeData] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(60);

  // Determine which payment flow was used
  const paymentFlow = location.state?.paymentFlow; // 'nfc' or 'qr'
  const testingMode = location.state?.testingMode || false;

  useEffect(() => {
    const transactionId = location.state?.transactionId || new URLSearchParams(location.search).get('transactionId');
    const ticketFromState = location.state?.ticket;

    console.log('[ReceiptPage] Init - paymentFlow:', paymentFlow, 'testingMode:', testingMode, 'transactionId:', transactionId);

    if (paymentFlow === 'qr' && transactionId) {
      // QR flow - always fetch and show ticket (user needs to scan from kiosk screen)
      fetchTicket(transactionId);
    } else if (ticketFromState) {
      // NFC flow - ticket passed directly from payment flow
      setTicket(ticketFromState);
      setQrCodeData(ticketFromState.qr_code || '');
      setIsLoading(false);
    } else if (transactionId) {
      // Fetch ticket by transaction ID (fallback)
      fetchTicket(transactionId);
    } else {
      navigate('/');
    }
  }, [location.state, location.search, navigate, paymentFlow]);

  // Countdown timer - auto redirect after 60 seconds
  useEffect(() => {
    if (countdown <= 0) {
      navigate('/');
      return;
    }

    const timer = setInterval(() => {
      setCountdown((prev) => prev - 1);
    }, 1000);

    return () => {
      clearInterval(timer);
    };
  }, [countdown, navigate]);

  const fetchTicket = async (transactionId: string) => {
    try {
      const ticketData = await transactionService.getTicket(transactionId);
      setTicket(ticketData);
      setQrCodeData(ticketData.qr_code || '');
    } catch (err: any) {
      console.error('Failed to fetch ticket:', err);
      const errorMessage = err.response?.data?.message || 'Failed to load ticket information';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleClose = () => {
    navigate('/');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-white">Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <p className="text-white text-xl mb-4">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700"
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  // Show ticket with QR code for user to scan
  if (!ticket) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <p className="text-white text-xl mb-4">No ticket data found</p>
          <button
            onClick={() => navigate('/')}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700"
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-gray-900 to-slate-800 screen-only">
      {/* Modern Header */}
      <div className="bg-black/20 backdrop-blur-md border-b border-white/10 py-6 px-6">
        <div className="max-w-4xl mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center shadow-lg">
              <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <div className="flex items-center space-x-3">
                <h1 className="text-3xl md:text-4xl font-bold text-white">
                  YOUR TICKET
                </h1>
                {testingMode && (
                  <span className="px-3 py-1 bg-orange-500 text-white rounded-full text-sm font-semibold">
                    TEST MODE
                  </span>
                )}
              </div>
              <p className="text-gray-400 text-sm md:text-base mt-1">
                Payment successful
              </p>
            </div>
          </div>
          <div className="text-center bg-white/10 backdrop-blur-sm rounded-xl px-4 py-2 border border-white/20">
            <div className="text-xs text-gray-400 uppercase tracking-wide">Auto-close</div>
            <div className={`text-2xl font-bold ${countdown <= 10 ? 'text-red-400 animate-pulse' : 'text-white'}`}>
              {countdown}s
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Receipt Card */}
        <div className="bg-white/95 backdrop-blur-md rounded-3xl shadow-2xl p-8 border border-white/20">

          {/* Ticket Number - Prominent */}
          <div className="text-center mb-6 pb-6 border-b border-gray-200">
            <div className="text-xs text-gray-500 uppercase tracking-wide mb-2">Ticket Number</div>
            <div className="font-mono font-bold text-lg text-gray-900 break-all">{ticket.number}</div>
          </div>

          {/* Two Column Layout */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">

            {/* Left Column - Ticket Details */}
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-800 text-sm uppercase tracking-wide">Ticket Details</h3>

              <div className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-600 text-sm">Type:</span>
                  <span className="font-semibold text-sm capitalize">{ticket.type}</span>
                </div>

                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-600 text-sm">Price:</span>
                  <span className="font-bold text-lg text-green-600">&euro;{parseFloat(String(ticket.price || 0)).toFixed(2)}</span>
                </div>

                <div className="flex justify-between items-center py-2">
                  <span className="text-gray-600 text-sm">Status:</span>
                  <span className={`px-2 py-1 rounded text-xs font-semibold ${
                    ticket.status === 'active'
                      ? 'bg-green-100 text-green-800'
                      : ticket.status === 'used'
                      ? 'bg-gray-100 text-gray-800'
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {ticket.status?.toUpperCase()}
                  </span>
                </div>
              </div>
            </div>

            {/* Right Column - QR Code */}
            <div className="text-center">
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                {qrCodeData ? (
                  <div
                    className="w-48 h-48 mx-auto"
                    dangerouslySetInnerHTML={{ __html: qrCodeData }}
                  />
                ) : (
                  <div className="w-48 h-48 bg-gray-200 rounded-lg flex items-center justify-center mx-auto">
                    <span className="text-gray-500 text-xs">QR Code not available</span>
                  </div>
                )}
              </div>
              <p className="text-xs text-gray-600 mt-3">
                Scan with your phone to save your ticket
              </p>
            </div>
          </div>

          {/* Instructions */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <h3 className="font-semibold text-blue-800 text-sm mb-2">
              Save Your Ticket
            </h3>
            <ul className="text-blue-700 text-xs space-y-1">
              <li>1. Take a photo of this screen OR</li>
              <li>2. Scan the QR code above with your phone to view and save your ticket</li>
              <li>3. Present the QR code for validation at the venue</li>
            </ul>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={handlePrint}
              className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-10 py-4 rounded-2xl hover:from-blue-700 hover:to-blue-800 transition-all duration-300 font-semibold text-lg flex items-center justify-center space-x-3 shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
              </svg>
              <span>Print Ticket</span>
            </button>
            <button
              onClick={handleClose}
              className="bg-gradient-to-r from-gray-600 to-gray-700 text-white px-10 py-4 rounded-2xl hover:from-gray-700 hover:to-gray-800 transition-all duration-300 font-semibold text-lg flex items-center justify-center space-x-3 shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
              <span>Back to Home</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    {/* Printable version - hidden on screen, revealed only when printing */}
    {ticket && <PrintableTicket ticket={ticket} qrCodeData={qrCodeData} />}

    {/* Hide the on-screen receipt while printing */}
    <style>{`
      @media print {
        .screen-only { display: none !important; }
      }
    `}</style>
    </>
  );
};

export default ReceiptPage;
